"use client";

import React, { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import ServiceCard from "../cards/serviceCard";
import CoreServicesSectionHeader from "@/components/coreServicesSectionHeader";
import CoreServicesIntroBox from "@/components/coreServicesIntroBox";
import { cn } from "@/lib";
import { getInquiriesAdapter } from "@/lib/adapters/getInquiriesAdapter";
import AppContainer from "../appContainer";
import { IRawInquiriesResData, IServiceCard } from "@/interfaces";
import { freeServices } from "@/constants";

const VARIANT_ICON_COLOR: Record<string, string> = {
  "emerald-mint": "text-emerald-mint!",
  "sky-bliss": "text-sky-bliss!",
  "teal-ocean": "text-teal-ocean!",
  "sunset-gold": "text-sunset-gold!",
  "yellow-500": "text-yellow-500!",
  "rose-500": "text-rose-500!",
};

const VARIANT_ICON_BG: Record<string, string> = {
  "emerald-mint": "bg-emerald-mint/10 text-emerald-mint",
  "sky-bliss": "bg-sky-bliss/10 text-sky-bliss",
  "teal-ocean": "bg-teal-ocean/10 text-teal-ocean",
  "sunset-gold": "bg-sunset-gold/10 text-sunset-gold",
  "yellow-500": "bg-yellow-500/10 text-yellow-500",
  "rose-500": "bg-rose-500/10 text-rose-500",
};

function mergeStaticFreeServices(coreServices: IServiceCard[]): IServiceCard[] {
  const dynamicFlags = new Set(
    coreServices.flatMap((card) =>
      card.services.map((service) => service.flag).filter(Boolean),
    ),
  );
  const freeFallbackCards = freeServices
    .map((card) => ({
      ...card,
      services: card.services.filter(
        (service) => !service.flag || !dynamicFlags.has(service.flag),
      ),
    }))
    .filter((card) => card.services.length > 0);

  if (!freeFallbackCards.length) {
    return coreServices;
  }

  const [freeFallbackCard] = freeFallbackCards;
  const freeCardIndex = coreServices.findIndex(
    (card) => card.category === "freeServices",
  );

  if (freeCardIndex === -1) {
    return [...coreServices, ...freeFallbackCards];
  }

  return coreServices.map((card, index) =>
    index === freeCardIndex
      ? {
          ...card,
          services: [...card.services, ...freeFallbackCard.services],
        }
      : card,
  );
}

const CoreServicesSection = ({
  servicesData,
}: {
  servicesData: IRawInquiriesResData | null;
}) => {
  const t = useTranslations("services");
  const tc = useTranslations("categories");
  const [activeTab, setActiveTab] = useState<string>("all");
  const [contentOpacity, setContentOpacity] = useState(1);

  const coreServicesData = useMemo(
    () => mergeStaticFreeServices(getInquiriesAdapter(servicesData)),
    [servicesData],
  );
  if (!servicesData || !coreServicesData.length) return null;

  const filteredData =
    activeTab === "all"
      ? coreServicesData
      : coreServicesData.filter((card) => card.id === activeTab);

  const isSingleCategory = activeTab !== "all" && filteredData.length === 1;

  const changeTab = (id: string) => {
    if (id === activeTab) return;
    setContentOpacity(0);
    setTimeout(() => {
      setActiveTab(id);
      setContentOpacity(1);
    }, 180);
  };

  const gridCols =
    filteredData.length === 2
      ? "grid-cols-1 sm:grid-cols-2"
      : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  return (
    <section className="mb-20 lg:mb-30">
      <AppContainer className="h-auto">
        <div className="mx-auto w-full">
          <CoreServicesSectionHeader
            title={t("title")}
            description={t("description")}
          />

          {/* Category Filter Tabs */}
          <div className="scrollbar-hide mb-8 flex gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => changeTab("all")}
              className={cn(
                "flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200",
                activeTab === "all"
                  ? "bg-primary-main text-white shadow-md"
                  : "hover:border-primary-main hover:text-primary-main border border-gray-200 bg-white text-gray-600",
              )}
            >
              همه خدمات
            </button>

            {coreServicesData.map((card) => (
              <button
                key={card.id}
                onClick={() => changeTab(card.id)}
                className={cn(
                  "flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200",
                  activeTab === card.id
                    ? "bg-primary-main text-white shadow-md"
                    : "hover:border-primary-main hover:text-primary-main border border-gray-200 bg-white text-gray-600",
                )}
              >
                {card.categoryIcon &&
                  React.cloneElement(card.categoryIcon, {
                    size: 16,
                    className:
                      activeTab === card.id
                        ? "text-white!"
                        : VARIANT_ICON_COLOR[card.variant],
                  })}
                <span>{tc(card.category as any)}</span>
              </button>
            ))}
          </div>

          {/* Content — fades on tab change */}
          <div
            className="transition-opacity duration-[180ms] ease-in-out"
            style={{ opacity: contentOpacity }}
          >
            {isSingleCategory ? (
              filteredData.map((card) => (
                <div key={card.id}>
                  {/* Tablet / Desktop — expanded icon grid (quickaccess style) */}
                  <ul className="hidden grid-cols-4 gap-x-2 gap-y-2 sm:grid md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8">
                    {card.services.map((service, idx) => (
                      <li key={idx}>
                        <Link
                          id={`service-card-${service.flag}`}
                          href={
                            service.disabled !== "unavailability"
                              ? service.href
                              : "#"
                          }
                          target="_blank"
                          rel="noopener"
                          title={service.label}
                          aria-disabled={service.disabled === "unavailability"}
                          className={cn(
                            "group flex flex-col items-center gap-2 rounded-xl p-3 text-center transition-all duration-200 hover:bg-gray-50",
                            service.disabled === "unavailability" &&
                              "pointer-events-none opacity-40",
                          )}
                        >
                          {/* Icon square with tag badge outside overflow */}
                          <div className="relative">
                            <div
                              className={cn(
                                "flex h-14 w-14 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-110 lg:h-16 lg:w-16",
                                VARIANT_ICON_BG[card.variant],
                              )}
                            >
                              {React.cloneElement(service.icon, {
                                className: "size-7 lg:size-8",
                              })}
                            </div>
                            {service.tag &&
                              service.disabled !== "unavailability" && (
                                <span
                                  className={cn(
                                    "absolute -top-2 -right-2 rounded-full px-1.5 py-0.5 text-[8px] font-semibold text-white",
                                    service.tag === "new"
                                      ? "bg-red-500"
                                      : "bg-primary-main",
                                  )}
                                >
                                  {service.tag === "new" ? "جدید" : "به زودی"}
                                </span>
                              )}
                          </div>

                          {/* Label below the square */}
                          <span className="line-clamp-2 text-[11px] leading-tight font-medium text-gray-600 transition-colors group-hover:text-gray-800">
                            {service.label}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>

                  {/* Mobile — keep the list card */}
                  <div className="sm:hidden">
                    <ServiceCard {...card} />
                  </div>
                </div>
              ))
            ) : (
              <div className={cn("grid gap-6", gridCols)}>
                {filteredData.map((card) => (
                  <ServiceCard
                    key={card.id}
                    id={card.id}
                    category={card.category}
                    services={card.services}
                    variant={card.variant}
                    categoryIcon={card.categoryIcon}
                  />
                ))}
              </div>
            )}
          </div>

          <CoreServicesIntroBox
            description={t("introBoxDescription")}
            buttonlabel={t("allServices")}
            className="mt-14"
          />
        </div>
      </AppContainer>
    </section>
  );
};

export default CoreServicesSection;
